/**
 * Phase 1.7 — KPI tile with split-metric layout.
 *
 * Tend's dashboard runs a strip of these across the top of every module:
 * a primary number on the left, a smaller secondary number on the right,
 * separated by a vertical border. Captions sit beneath. Per the spec:
 *
 *   Animals      Active   |  Sold/Deceased
 *   Vehicles     In Service | Maintenance
 *   Inventory    In Stock | Below Threshold
 *   Tasks        Complete | Incomplete (this week)
 *   Documents    Processed | Needs Review
 *
 * The trend slot is optional — if a delta is supplied, it appears below
 * the primary value with up/down arrow + percent. Color tokens are moss
 * for positive, clay for negative; never raw red/green.
 */

import { ArrowDown, ArrowUp, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from './Card';

export interface KpiSplit {
  label: string;
  value: number | string;
  tone?: 'default' | 'warning' | 'positive';
}

export interface KpiCardProps {
  label: string;
  icon?: LucideIcon;
  primary: KpiSplit;
  secondary?: KpiSplit;
  trend?: { delta_pct: number; direction: 'up' | 'down' };
  hint?: string;
  className?: string;
  onClick?: () => void;
}

const TONE_CLASSES: Record<NonNullable<KpiSplit['tone']>, string> = {
  default: 'text-charcoal dark:text-charcoal-dark',
  warning: 'text-clay dark:text-clay-dark',
  positive: 'text-moss dark:text-moss-dark',
};

export function KpiCard({
  label,
  icon: Icon,
  primary,
  secondary,
  trend,
  hint,
  className,
  onClick,
}: KpiCardProps): JSX.Element {
  return (
    <Card
      compact
      interactive={Boolean(onClick)}
      onClick={onClick}
      className={cn('h-full', className)}
      header={
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="h-4 w-4 text-muted-foreground" /> : null}
          <span className="font-mono text-2xs uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
        </div>
      }
    >
      <div className={cn('flex items-stretch', secondary && 'divide-x divide-border')}>
        <Split split={primary} prominent />
        {secondary ? <Split split={secondary} /> : null}
      </div>
      {trend ? (
        <div
          className={cn(
            'mt-3 inline-flex items-center gap-1 text-xs font-medium',
            trend.direction === 'up'
              ? 'text-moss dark:text-moss-dark'
              : 'text-clay dark:text-clay-dark',
          )}
        >
          {trend.direction === 'up' ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )}
          {trend.delta_pct.toFixed(1)}%
        </div>
      ) : null}
      {hint ? <p className="mt-3 text-xs text-muted-foreground">{hint}</p> : null}
    </Card>
  );
}

function Split({ split, prominent }: { split: KpiSplit; prominent?: boolean }): JSX.Element {
  return (
    <div className={cn('flex flex-col', prominent ? 'flex-1 pr-4' : 'flex-1 pl-4')}>
      <span
        className={cn(
          'font-display tabular-nums leading-none',
          prominent ? 'text-3xl' : 'text-2xl',
          TONE_CLASSES[split.tone ?? 'default'],
        )}
      >
        {split.value}
      </span>
      <span className="mt-2 text-xs text-muted-foreground">{split.label}</span>
    </div>
  );
}
